package games.polyclub.website.frontend.store.pages

import adminlte.layout.Layout
import adminlte.layout.LayoutConfig
import adminlte.layout.LayoutOptions
import games.polyclub.frontend.lib.messagerenderer.MessageRendererMode
import games.polyclub.frontend.lib.messagerenderer.m
import games.polyclub.frontend.lib.react.bootstrap.components.BAlert
import games.polyclub.frontend.lib.react.bootstrap.cpfinput.CPFInput
import games.polyclub.frontend.lib.react.bootstrap.css.utility.BootstrapColor
import games.polyclub.frontend.lib.react.bootstrap.custom.components.EmailInput
import games.polyclub.frontend.lib.react.bootstrap.custom.components.ValidatingInput
import games.polyclub.frontend.lib.react.ga4.GA4
import games.polyclub.frontend.lib.react.hcaptcha.async.AsyncHCaptcha
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.classSet
import games.polyclub.frontend.lib.react.utils.classes
import games.polyclub.frontend.store.shared.RegisterRequest
import games.polyclub.message.Pronoun
import games.polyclub.message.div
import games.polyclub.message.unaryMinus
import games.polyclub.service.config.api.SecretString.Companion.asSecret
import games.polyclub.service.player.id.api.PlayerId
import games.polyclub.tool.functional.Option
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.components.Loading
import games.polyclub.website.frontend.store.components.MinecraftInput
import games.polyclub.website.frontend.store.context.useCart
import games.polyclub.website.frontend.store.hooks.*
import games.polyclub.website.frontend.store.pages.logged.validPasswordRegex
import games.polyclub.website.frontend.store.sitemap.LoggedSitemap
import games.polyclub.website.frontend.store.sitemap.PublicSitemap
import jquery.jQuery
import js.core.jso
import kotlinx.coroutines.launch
import react.*
import react.dom.events.FormEventHandler
import react.router.dom.Link
import react.router.useLocation
import react.router.useNavigate
import remix.run.router.LocationState
import web.cssom.pct
import web.cssom.rem
import web.dom.document
import web.html.ButtonType
import web.html.HTMLFormElement
import web.html.InputType
import web.window.WindowTarget

external interface RegisterLocationState: LocationState {
    var returnLocation: String?
    var returnState: LocationState?
}

val RegisterPage = SFC<Props> { _ ->
    val authCtx = useAuth()
    val cartCtx = useCart()
    val userPreferencesCtx = useUserPreferences()

    val navigate = useNavigate()
    val location = useLocation()
    val locationState = location.state?.unsafeCast<RegisterLocationState>()
    val goBack = useCallback(authCtx, locationState) {
        navigate(locationState?.returnLocation ?: LoggedSitemap.HOME.path, jso {
            state = locationState?.returnState
        })
    }

    val hCaptchaRef = useRef<AsyncHCaptcha>()
    val confirmPasswordRef = useRef<ValidatingInput>()

    val (pronoun, setPronoun) = useState<Option<Pronoun>?>(null)
    val (brazilian, setBrazilian) = useState(true)
    val (formValid, setFormValid) = useState(false)
    val (agreeTerms, setAgreeTerms) = useState(false)
    val (nick, setNick) = useState<String?>(cartCtx.selfIdentification?.name ?: "")
    val (playerId, setPlayerId) = useState<PlayerId?>(null)
    val (userValid, setUserValid) = useState<Boolean?>(null)
    val (password, setPassword) = useState("".asSecret())
    val (passwordValid, setPasswordValid) = useState<Boolean?>(null)
    val (passwordConfirm, setPasswordConfirm) = useState("".asSecret())
    val (passwordConfirmValid, setPasswordConfirmValid) = useState<Boolean?>(null)
    val (name, setName) = useState("")
    val (nameValid, setNameValid) = useState<Boolean?>(null)
    val (lastName, setLastName) = useState("")
    val (lastNameValid, setLastNameValid) = useState<Boolean?>(null)
    val (cpf, setCpf) = useState("".asSecret())
    val (cpfValid, setCpfValid) = useState<Boolean?>(null)
    val (email, setEmail) = useState("".asSecret())
    val (emailValid, setEmailValid) = useState<Boolean?>(null)
    val (hCaptchaToken, setHCaptchaToken) = useState<String?>(null)
    val (submitting, setSubmitting) = useState(false)
    val (error, setError) = useState("")

    val createRequest = useCallback(
        formValid, nick, playerId, password, name, lastName,
        cpf, email, hCaptchaToken, agreeTerms, brazilian, pronoun
    ) {
        if (!formValid) null
        else RegisterRequest(
            pronoun = pronoun,
            brazilian = brazilian,
            nick = nick!!,
            playerId = playerId,
            password = password,
            name = name,
            lastName = lastName,
            cpf = cpf.takeIf { brazilian },
            email = email,
            captcha = hCaptchaToken!!.asSecret(),
            agreeWithTerms = agreeTerms,
        )
    }

    val submit = useCallback(createRequest, submitting, setSubmitting) {
        if (submitting) {
            return@useCallback
        }
        val request = createRequest() ?: return@useCallback
        hCaptchaRef.current?.hCaptchaRef?.current?.resetCaptcha()
        setHCaptchaToken(null)
        setSubmitting(true)
        appScope.launch {
            try {
                StoreBff.register(request).also { result ->
                    setError(result.errorMessage ?: "")
                    if (!result.errorMessage.isNullOrBlank()) {
                        logEvent(
                            category = GACategory.REGISTRATION,
                            action = GAAction.REGISTRATION_FAILED,
                            label = "Tentou se registrar mas falhou"
                        )
                    }
                    result.autoLogin?.let { login ->
                        GA4.set(jso {
                            this.userId = login.profile.id.toInt()
                        })
                        logEvent(
                            category = GACategory.REGISTRATION,
                            action = GAAction.REGISTRATION,
                            label = "Se registrou"
                        )
                        authCtx.setAuth(login)
                        goBack()
                    }
                }
            } catch (e: Throwable) {
                logEvent(
                    category = GACategory.REGISTRATION,
                    action = GAAction.REGISTRATION_FAILED,
                    label = "Tentou se registrar mas ocorreu um erro"
                )
                e.printStackTrace()
                setError("Ocorreu um erro ao enviar o formulário")
            }
        }.also {
            it.invokeOnCompletion {
                setSubmitting(false)
            }
        }
    }

    val onFormSubmit = useCallback<FormEventHandler<HTMLFormElement>>(submit) { ev ->
        ev.preventDefault()
        submit()
    }

    val validatePasswordConfirmation = useCallback<suspend (String) -> Boolean>(password) { check ->
        check == password.secret
    }

    useEffect(
        setFormValid, userValid, passwordValid, passwordConfirmValid,
        nameValid, lastNameValid, cpfValid, emailValid, hCaptchaToken,
        agreeTerms, brazilian
    ) {
        console.log(
            "userValid", userValid,
            "passwordValid", passwordValid,
            "passwordConfirmValid", passwordConfirmValid,
            "nameValid", nameValid,
            "lastNameValid", lastNameValid,
            "cpfValid", !brazilian || (cpfValid ?: false),
            "emailValid", emailValid,
            "hCaptchaToken", hCaptchaToken,
            "agreeTerms", agreeTerms,
        )
        setFormValid(userValid == true && passwordValid == true &&
                passwordConfirmValid == true && nameValid == true &&
                lastNameValid == true && (!brazilian || (cpfValid ?: false)) && agreeTerms &&
                emailValid == true && !hCaptchaToken.isNullOrBlank())
    }

    useEffect(goBack, authCtx, authCtx.auth) {
        if (authCtx.auth != null) {
            goBack()
        } else {
            document.body.style.height = ""
            jQuery("#wrapper").addClass("login-box")
            jQuery("body").Layout(jso<LayoutOptions> {
                loginRegisterAutoHeight = true
            }).addClass("login-page")
                .Layout(LayoutConfig.INIT)
                .Layout(LayoutConfig.FIX_LOGIN_REGISTER_HEIGHT)
            cleanup {
                jQuery("body").removeClass("login-page")
                jQuery("#wrapper").removeClass("login-box")
            }
        }
    }
    div("card card-outline card-primary") {
        div("card-header text-center") {
            Link("h1") {
                to = PublicSitemap.HOME.path
                img {
                    style = jso {
                        width = 100.pct
                    }
                    src = "img/logo.png"
                }
            }
        }
        div("card-body") {
            if (!submitting) {
                classSet += "d-none"
            }
            Loading()
        }
        div("card-body") {
            if (submitting) {
                classSet += "d-none"
            }
            if (error.isNotBlank()) {
                BAlert {
                    color = BootstrapColor.DANGER
                    onClose = { setError("") }
                    +error
                }
            }
            p("login-box-msg") {
                +"Crie uma senha com no mínimo 6 caractéres, um número, letra maiúscula, minúscula e um símbolo."
            }
            form {
                noValidate = true
                onSubmit = onFormSubmit
                div("input-group mb-3") {
                    select("form-control") {
                        if (pronoun != null) {
                            classSet += "is-valid"
                        }
                        value = when {
                            pronoun == null -> ""
                            pronoun.isEmpty -> "*"
                            else -> pronoun.get().name
                        }
                        onChange = { ev ->
                            setPronoun(when (val value = ev.target.value) {
                                "" -> null
                                "*" -> Option.empty()
                                else -> Option.of(Pronoun.valueOf(value))
                            })
                            userPreferencesCtx.changePreferences { current ->
                                current.copy(pronoun = pronoun)
                            }
                        }
                        option {
                            value = ""
                            +"Quais seus pronomes?"
                        }
                        Pronoun.entries.forEach { pronoun ->
                            option {
                                value = pronoun.name
                                m(-"${"Ela"/pronoun}/${"Dela"/pronoun}", MessageRendererMode.RAW)
                            }
                        }
                        option {
                            value = "*"
                            +"Qualquer pronome"
                        }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-question-circle") {}
                        }
                    }
                }
                MinecraftInput("mb-3") {
                    placeholder = "Apelido no Minecraft"
                    required = true
                    value = nick
                    hideValidPrepend = true
                    iconAtRight = true
                    onChange = { setNick(it) }
                    onValidationChange = { _, valid, id ->
                        setUserValid(valid)
                        setPlayerId(id)
                    }
                }
                div("input-group mb-3") {
                    ValidatingInput::class {
                        classes = "form-control"
                        type = InputType.password
                        placeholder = "Crie uma senha"
                        required = true
                        minLength = 6
                        value = password.secret
                        validator = { it.matches(validPasswordRegex) }
                        onChange = {
                            setPassword(it.target.value.asSecret())
                            appScope.launch {
                                confirmPasswordRef.current?.revalidate(true)?.await()
                            }
                        }
                        onValidationStateChange = { ev ->
                            setPasswordValid(ev.current)
                        }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-lock") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    ValidatingInput::class {
                        classes = "form-control"
                        ref = confirmPasswordRef
                        type = InputType.password
                        placeholder = "Repita a senha"
                        required = true
                        minLength = 6
                        value = passwordConfirm.secret
                        onChange = { setPasswordConfirm(it.target.value.asSecret()) }
                        validator = validatePasswordConfirmation
                        onValidationStateChange = { setPasswordConfirmValid(it.current) }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-lock") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    ValidatingInput::class {
                        classes = "form-control"
                        type = InputType.text
                        placeholder = "Nome"
                        required = true
                        minLength = 3
                        value = name
                        onChange = { setName(it.target.value) }
                        onValidationStateChange = { setNameValid(it.current) }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-user") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    ValidatingInput::class {
                        classes = "form-control"
                        type = InputType.text
                        placeholder = "Sobrenome"
                        required = true
                        minLength = 3
                        value = lastName
                        onChange = { setLastName(it.target.value) }
                        onValidationStateChange = { setLastNameValid(it.current) }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-user") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    select("form-control is-valid") {
                        value = brazilian.toString()
                        onChange = { ev ->
                            val br = ev.target.value.toBoolean()
                            setBrazilian(br)
                            if (!br) {
                                setCpf("".asSecret())
                            }
                        }
                        option {
                            value = "true"
                            m(-"Sou ${"brasileira"/pronoun}", MessageRendererMode.RAW)
                        }
                        option {
                            value = "false"
                            m(-"Não sou ${"brasileira"/pronoun}", MessageRendererMode.RAW)
                        }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-flag") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    CPFInput::class {
                        classes = "form-control"
                        type = InputType.text
                        placeholder = "CPF"
                        required = brazilian
                        disabled = !brazilian
                        value = cpf.secret
                        onChange = { setCpf(it.target.value.asSecret()) }
                        onValidationStateChange = { setCpfValid(it.current) }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            style = jso {
                                paddingRight = 0.65.rem
                                paddingLeft = 0.65.rem
                            }
                            span("fas fa-id-card") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    EmailInput::class {
                        classes = "form-control"
                        type = InputType.text
                        placeholder = "E-mail"
                        required = true
                        value = email.secret
                        onChange = { setEmail(it.target.value.asSecret()) }
                        onValidationStateChange = { setEmailValid(it.current) }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-envelope") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    div("text-center m-auto") {
                        AsyncHCaptcha::class {
                            siteKeyLoader = {
                                StoreBff.loadHCaptchaKey()
                            }
                            onVerify = { token, _ ->
                                setHCaptchaToken(token)
                            }
                            if (document.body.className.contains("dark-mode")) {
                                theme = "dark"
                            }
                        }
                    }
                }
                div("row") {
                    div("col-8") {
                        div("icheck-primary") {
                            input {
                                type = InputType.checkbox
                                id = "agreeTerms"
                                checked = agreeTerms
                                onChange = { ev ->
                                    setAgreeTerms(ev.target.checked)
                                }
                            }
                            label {
                                htmlFor = "agreeTerms"
                                +"Concordo com os "
                                Link {
                                    to = PublicSitemap.TERMS.path
                                    target = WindowTarget._blank
                                    +"termos"
                                }
                            }
                        }
                    }
                    div("col-4") {
                        button("btn btn-primary btn-block") {
                            type = ButtonType.submit
                            disabled = !formValid
                            +"Cadastrar"
                        }
                    }
                }
                div {
                    button("btn btn-outline-secondary btn-block mt-2") {
                        type = ButtonType.button
                        onClick = {
                            goBack()
                        }
                        +"Já tenho cadastro"
                    }
                }
            }
        }
    }
}
